import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useEffect, useMemo, useState } from 'react';
import { useGetLimitUsageForHubQuery } from '../../__generated__/chirp/com/hubspot/feature/store/rpc/FeatureStoreUsageRpc.hooks';
import withErrorBoundary from '../components/WithErrorBoundary';
import { useIsInActiveTrial } from '../hooks/useIsInActiveTrial';
import { filterLimits, sortLimitsByHigherUsage } from '../utils/usageLimitUtils';
import LimitCard from './LimitCard.tsx';

/*
This component should only be used to show to free users in a scenario where
we are telling them to upgrade to crm-suite-starter-bundle. The BE data is set up
for this narrow scenario. The limits shown are 'free' limits, and the nextTierLimits
represent what the limits would be increased to if crm-suite-starter-bundle is
purchased. We are working on extending the BE to support additional scenarios.
*/
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LimitUsageSection = ({
  isFreePortal,
  onExpandButtonClick,
  onCollapseButtonClick,
  useCompactGrid = false
}) => {
  const isInTrial = useIsInActiveTrial();
  const {
    loading,
    data: limitUsagesData
  } = useGetLimitUsageForHubQuery({
    variables: {
      upgradeApiName: 'crm-suite-starter-bundle'
    }
  });
  const limitUsages = useMemo(() => {
    if (loading || !limitUsagesData) {
      return [];
    }
    return sortLimitsByHigherUsage(filterLimits(limitUsagesData.limitUsages));
  }, [loading, limitUsagesData]);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const columnCount = useMemo(() => {
    if (useCompactGrid) {
      return screenWidth >= 900 ? 2 : 1;
    }
    return 2;
  }, [screenWidth, useCompactGrid]);

  //Usage data is not accurate for paid or trialing portals
  if (!isFreePortal || isInTrial) return null;
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 400,
      layout: "centered"
    });
  }
  if (!limitUsages.length) return null;
  return /*#__PURE__*/_jsxs(View, {
    marginBlock: "600",
    children: [/*#__PURE__*/_jsx(H2, {
      "aria-level": 2,
      typography: "heading-600",
      marginBlockEnd: "800",
      maxInlineSize: "900px",
      marginInline: "auto",
      inlineSize: "fit-content",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "limitUsageComponents.header"
      })
    }), /*#__PURE__*/_jsx(ExpandableText
    //height to allow 2 stacked cards
    , {
      maxHeight: 570,
      expandButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "limitUsageComponents.expandCards"
      }),
      collapseButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "limitUsageComponents.collapseCards"
      }),
      onExpandButtonClick: onExpandButtonClick,
      onCollapseButtonClick: onCollapseButtonClick,
      fadeOutHeight: 0,
      children: /*#__PURE__*/_jsx(CardGrid
      //small padding so the grid dropshadow is not hidden by the expandable container
      , {
        padding: "100",
        gutterSize: 16,
        columns: columnCount,
        expandToFill: true,
        children: limitUsages.map(limit => {
          return /*#__PURE__*/_jsx(LimitCard, {
            limit: limit
          }, limit.limitId);
        })
      })
    })]
  });
};
export default withErrorBoundary(LimitUsageSection, {
  boundaryName: 'LimitUsageSection'
});